import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api, type AccommodationDto, type BlockDto, type UnitDto, type UnitGroupDto,
} from '../lib/api';
import { toDay, toIso } from '../lib/format';
import { useKeyMap, type Binding } from '../lib/keys';
import { useT, type MessageKey, type TFunction } from '../lib/i18n';

/**
 * Units, terrains and blocks.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS SCREEN IS NOT OPTIONAL
 * ---------------------------------------------------------------------------
 *
 * Availability in this product is not derived from bookings — it is what
 * reception has said is unavailable. A pitch under maintenance, an annual plot
 * sold for the year, a field with no hard standing that shuts for the winter:
 * each is a row somebody typed, and the website reflects them directly. So a
 * park with no way to enter a block cannot operate, however good its prices
 * are.
 *
 * That shapes the layout. Blocking is the most-used action on the screen and it
 * happens while somebody is on the phone, so the block form sits FIRST and takes
 * a pitch by the name reception says out loud — "Berkenlaan 12" — rather than
 * making them find it in a list of several hundred. Everything else on the
 * screen is setup work that happens once.
 *
 * ---------------------------------------------------------------------------
 * ONE FETCH, LOCAL FILTERING
 * ---------------------------------------------------------------------------
 *
 * The whole park's units and blocks are loaded once rather than per
 * accommodation. A real park is a few hundred units and a few hundred blocks —
 * smaller than one season's rate cards, which this dashboard already holds — and
 * it buys two things worth more than the bytes: switching accommodation is
 * instant, and an address can be checked for a clash against the WHOLE park,
 * which is the scope the database's unique index actually has.
 */

/**
 * What the API's own words arrive through. Errors are the caller's to show.
 *
 * The success notice may be a function of the result, because "added 47 units"
 * is not knowable until the server says how many of the range were free.
 */
type Run = <T>(fn: () => Promise<T>, ok?: string | ((out: T) => string)) => Promise<T | null>;

export interface UnitsProps {
  accommodations: AccommodationDto[];
  accommodationId: string | null;
  onAccommodation: (id: string) => void;
  run: Run;
  busy?: boolean;
}

/**
 * Why a unit is not for sale.
 *
 * ---------------------------------------------------------------------------
 * THE API'S LIST, NOT A SECOND ONE
 * ---------------------------------------------------------------------------
 *
 * This used to be its own set of five, written on the belief that the API stored
 * the reason as free text. It does not: `reason` reaches the PUBLIC change log
 * and cannot be retracted from it, so it is a closed vocabulary.
 *
 * The two lists had drifted, and in the worst possible direction — the DEFAULT
 * here was `blocked`, which the API refuses. So blocking a unit the ordinary way
 * (pick a pitch, pick dates, press the button) failed with a message about a
 * vocabulary the operator had never been shown, and two reasons the API does
 * accept were not offered at all.
 *
 * One list now, matching `BLOCK_REASONS` in the store, with a test tying them
 * together. Anything already stored that is not one of these is still shown
 * exactly as it was typed, because an old block is history and not a form.
 */
export const REASONS = [
  'maintenance', 'closed', 'contract', 'owner', 'seasonal', 'other',
] as const;

const REASON_LABEL: Record<string, MessageKey> = {
  maintenance: 'units.reasonMaintenance',
  closed: 'units.reasonClosed',
  contract: 'units.reasonContract',
  owner: 'units.reasonOwner',
  seasonal: 'units.reasonSeasonal',
  other: 'units.reasonOther',
  // Kept for blocks already stored under the dashboard's old vocabulary: it is
  // no longer offered, and a row carrying it still reads as words.
  blocked: 'units.reasonBlocked',
};

/** A reason in words where it is one of ours, and as typed where it is not. */
function reasonText(t: TFunction, reason: string): string {
  const key = REASON_LABEL[reason];
  return key ? t(key) : reason;
}

/** How many rows of a long terrain to draw before saying so. */
const ROW_CAP = 200;

const today = () => Math.floor(Date.now() / 86_400_000);

export function Units({ accommodations, accommodationId, onAccommodation, run, busy }: UnitsProps) {
  const t = useT();

  const [groups, setGroups] = useState<UnitGroupDto[]>([]);
  const [units, setUnits] = useState<UnitDto[]>([]);
  const [blocks, setBlocks] = useState<BlockDto[]>([]);
  const [loaded, setLoaded] = useState(false);

  const [terrain, setTerrain] = useState<string>('');
  const [focusUnit, setFocusUnit] = useState<string | null>(null);
  const [pastBlocks, setPastBlocks] = useState(false);

  const [editing, setEditing] = useState<UnitDto | null>(null);
  const [generating, setGenerating] = useState(false);
  const [terrainsOpen, setTerrainsOpen] = useState(false);
  const [taken, setTaken] = useState<string[]>([]);

  /* The block form's own state lives up here so a row's Block button can fill
     it in — which is the difference between four clicks and one. */
  const [blockUnit, setBlockUnit] = useState('');

  const reload = useCallback(async () => {
    const [g, u, b] = await Promise.all([api.unitGroups(), api.units(), api.blocks()]);
    setGroups(g);
    setUnits(u);
    setBlocks(b);
    setLoaded(true);
    return { g, u, b };
  }, []);

  useEffect(() => { void run(reload); }, [run, reload]);

  const groupLabel = useCallback(
    (id: string | null | undefined) => groups.find((g) => g.id === id)?.label ?? null,
    [groups],
  );

  const mine = useMemo(
    () => (accommodationId ? units.filter((u) => u.accommodationId === accommodationId) : []),
    [units, accommodationId],
  );

  /* Only the terrains this accommodation actually occupies: a filter offering
     forty terrains for a type that sits in two is a filter nobody reads. */
  const myTerrains = useMemo(() => {
    const ids = new Set(mine.map((u) => u.groupId ?? ''));
    return groups.filter((g) => ids.has(g.id));
  }, [mine, groups]);
  const anyUngrouped = useMemo(() => mine.some((u) => !u.groupId), [mine]);

  const shown = useMemo(() => {
    const rows = terrain === ''
      ? mine
      : mine.filter((u) => (u.groupId ?? '') === (terrain === 'none' ? '' : terrain));
    return rows;
  }, [mine, terrain]);

  const byUnit = useMemo(() => {
    const map = new Map<string, UnitDto>();
    for (const u of units) map.set(u.id, u);
    return map;
  }, [units]);

  /** Blocks of the units on screen, newest window last, past ones optional. */
  const shownBlocks = useMemo(() => {
    const ids = new Set((focusUnit ? shown.filter((u) => u.id === focusUnit) : shown).map((u) => u.id));
    const now = today();
    return blocks
      .filter((b) => ids.has(b.unitId))
      .filter((b) => pastBlocks || (toDay(b.until) ?? 0) > now)
      .sort((a, b) => a.from.localeCompare(b.from)
        || (byUnit.get(a.unitId)?.display ?? '').localeCompare(byUnit.get(b.unitId)?.display ?? ''));
  }, [blocks, shown, focusUnit, pastBlocks, byUnit]);

  const blockCount = useMemo(() => {
    const now = today();
    const counts = new Map<string, number>();
    for (const b of blocks) {
      if ((toDay(b.until) ?? 0) <= now) continue;
      counts.set(b.unitId, (counts.get(b.unitId) ?? 0) + 1);
    }
    return counts;
  }, [blocks]);

  const accommodation = accommodations.find((a) => a.id === accommodationId) ?? null;

  useKeyMap(useMemo<Binding[]>(() => [
    {
      combo: 'b',
      label: 'keys.focusBlockUnit',
      group: 'keys.groupUnits',
      run: () => { document.getElementById('blk-unit')?.focus(); },
    },
  ], []));

  const saveUnit = (u: UnitDto) => {
    void run(async () => {
      await api.putUnit(u);
      await reload();
      return true;
    }, t('app.unitSaved'));
    setEditing(null);
  };

  const deleteUnit = (unitId: string) => {
    void run(async () => {
      await api.deleteUnit(unitId);
      await reload();
      return true;
    }, t('app.unitDeleted'));
    setEditing(null);
  };

  return (
    <div className="stack">
      <BlockForm
        units={mine}
        allUnits={units}
        groupLabel={groupLabel}
        unitText={blockUnit}
        onUnitText={setBlockUnit}
        accommodation={accommodation}
        busy={busy}
        onSubmit={(b, unit) => {
          void run(async () => {
            await api.putBlock(b);
            await reload();
            return true;
          }, t('app.blockSaved', { unit: unit.display ?? unit.id, from: b.from, until: b.until }));
        }}
      />

      <section className="panel">
        <div className="panel-hd">
          <h2>{t('units.heading')}</h2>
          <span className="grow" />
          {accommodation && (
            <span className="note">
              {t('units.count', { count: mine.length })}
              {' · '}
              {t('units.activeCount', { count: mine.filter((u) => u.active !== false).length })}
            </span>
          )}
          <button type="button" className="btn" onClick={() => setTerrainsOpen((v) => !v)}>
            {t('units.terrains')}
          </button>
          <button
            type="button" className="btn" disabled={!accommodationId}
            onClick={() => { setTaken([]); setGenerating(true); }}
          >
            {t('units.generateRange')}
          </button>
          <button
            type="button" className="btn pri" disabled={!accommodationId}
            onClick={() => setEditing(blankUnit(accommodationId ?? '', terrain))}
          >
            {t('units.addUnit')}
          </button>
        </div>
        <div className="panel-bd">
          <p className="note" style={{ marginBottom: 12 }}>{t('units.intro')}</p>
          <div className="controls">
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label htmlFor="uacc">{t('app.accommodationHeading')}</label>
              <select
                id="uacc" value={accommodationId ?? ''}
                onChange={(e) => { onAccommodation(e.target.value); setTerrain(''); setFocusUnit(null); }}
              >
                <option value="">{t('app.chooseAccommodation')}</option>
                {accommodations.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="uterrain">{t('units.terrain')}</label>
              <select
                id="uterrain" value={terrain}
                onChange={(e) => { setTerrain(e.target.value); setFocusUnit(null); }}
                disabled={!accommodationId}
              >
                <option value="">{t('units.allTerrains')}</option>
                {myTerrains.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                {anyUngrouped && <option value="none">{t('units.noTerrain')}</option>}
              </select>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            {!accommodationId ? (
              <p className="note">{t('units.chooseAccommodation')}</p>
            ) : !loaded ? (
              <p className="note">{t('units.loading')}</p>
            ) : mine.length === 0 ? (
              <p className="note">
                {t('units.emptyForAccommodation', { label: accommodation?.label ?? '' })}
              </p>
            ) : (
              <>
                <div className="scroll-x list-wrap">
                  <table className="units">
                    <thead>
                      <tr>
                        <th>{t('units.columnUnit')}</th>
                        <th className="col-wide">{t('units.columnTerrain')}</th>
                        <th className="col-wide r">{t('units.columnNumber')}</th>
                        <th className="col-wide">{t('units.columnGuestMayPick')}</th>
                        <th>{t('units.columnStatus')}</th>
                        {/* Context on a phone, not identity: what is blocked is
                            listed in full in the panel below, and the actions
                            have to stay on screen. */}
                        <th className="col-wide">{t('units.columnBlocked')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {shown.slice(0, ROW_CAP).map((u) => (
                        <tr key={u.id} className={focusUnit === u.id ? 'picked' : undefined}>
                          <td>
                            {u.display ?? u.address ?? u.label ?? u.id}
                            {u.note && <div className="meta">{u.note}</div>}
                          </td>
                          <td className="col-wide">
                            {groupLabel(u.groupId)
                              ?? <span className="muted">{t('units.noTerrain')}</span>}
                          </td>
                          <td className="col-wide r">{u.number ?? ''}</td>
                          <td className="col-wide">
                            {u.selectable ? t('units.yes') : <span className="muted">{t('units.no')}</span>}
                          </td>
                          <td>
                            {u.active === false
                              ? <span className="chip bad">{t('units.statusRetired')}</span>
                              : <span className="chip">{t('units.statusActive')}</span>}
                          </td>
                          <td className="col-wide">
                            {blockCount.get(u.id)
                              ? (
                                <button
                                  type="button" className="btn"
                                  onClick={() => setFocusUnit(focusUnit === u.id ? null : u.id)}
                                >
                                  {t('units.blockCount', { count: blockCount.get(u.id)! })}
                                </button>
                              )
                              : <span className="muted">{t('units.noBlocks')}</span>}
                          </td>
                          <td className="r">
                            <button
                              type="button" className="btn pri"
                              onClick={() => {
                                setBlockUnit(u.address ?? u.label ?? u.id);
                                document.getElementById('blk-from')?.focus();
                              }}
                            >
                              {t('units.block')}
                            </button>
                            {' '}
                            <button type="button" className="btn" onClick={() => setEditing(u)}>
                              {t('units.edit')}
                            </button>
                            {' '}
                            <button
                              type="button" className="btn danger only-wide" disabled={busy}
                              onClick={() => deleteUnit(u.id)}
                            >
                              {t('units.delete')}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {shown.length > ROW_CAP && (
                  <p className="note" style={{ marginTop: 10 }}>
                    {t('units.truncated', { shown: ROW_CAP, total: shown.length })}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {accommodationId && loaded && mine.length > 0 && (
        <section className="panel">
          <div className="panel-hd">
            <h2>
              {focusUnit
                ? t('units.blocksForUnit', { unit: byUnit.get(focusUnit)?.display ?? focusUnit })
                : t('units.blocksHeading')}
            </h2>
            <span className="grow" />
            {focusUnit && (
              <button type="button" className="btn" onClick={() => setFocusUnit(null)}>
                {t('units.showEveryUnit')}
              </button>
            )}
            <label className="check">
              <input
                type="checkbox" checked={pastBlocks}
                onChange={(e) => setPastBlocks(e.target.checked)}
              />
              {t('units.includePast')}
            </label>
          </div>
          <div className="panel-bd">
            {shownBlocks.length === 0 ? (
              <p className="note">
                {pastBlocks ? t('units.noBlocksAtAll') : t('units.noBlocksAhead')}
              </p>
            ) : (
              <div className="scroll-x">
                <table className="blocks">
                  <thead>
                    <tr>
                      <th>{t('units.columnUnit')}</th>
                      <th>{t('units.columnNights')}</th>
                      <th className="col-wide r">{t('units.columnLength')}</th>
                      <th>{t('units.columnReason')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {shownBlocks.map((b) => {
                      const nights = (toDay(b.until) ?? 0) - (toDay(b.from) ?? 0);
                      return (
                        <tr key={b.id}>
                          <td>
                            {byUnit.get(b.unitId)?.display ?? b.unitId}
                            {/* The terrain is context and it is in the filter
                                above; the note in the reason column is content,
                                so on a phone this is what gives up its width. */}
                            <div className="meta col-wide">
                              {groupLabel(byUnit.get(b.unitId)?.groupId) ?? ''}
                            </div>
                          </td>
                          {/* Each date unbreakable, so a narrow column wraps
                              BETWEEN the two rather than inside one: a date cut
                              at its hyphen reads as two different dates. */}
                          <td className="num">
                            <span className="nowrap">{b.from}</span>
                            {' → '}
                            <span className="nowrap">{b.until}</span>
                          </td>
                          <td className="col-wide r">{t('quote.nightCount', { count: nights })}</td>
                          <td>
                            <span className="chip">{reasonText(t, b.reason)}</span>
                            {b.note && <div className="meta">{b.note}</div>}
                          </td>
                          <td className="r">
                            <button
                              type="button" className="btn danger" disabled={busy}
                              onClick={() => {
                                void run(async () => {
                                  await api.deleteBlock(b.id);
                                  await reload();
                                  return true;
                                }, t('app.blockRemoved'));
                              }}
                            >
                              {t('units.removeBlock')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {editing && (
        <UnitForm
          unit={editing}
          groups={groups}
          allUnits={units}
          busy={busy}
          onCancel={() => setEditing(null)}
          onSubmit={saveUnit}
          onDelete={editing.id ? () => deleteUnit(editing.id) : undefined}
        />
      )}

      {generating && accommodationId && (
        <GenerateForm
          accommodationId={accommodationId}
          groups={groups}
          preferGroup={terrain === 'none' ? '' : terrain}
          taken={taken}
          onCancel={() => { setGenerating(false); setTaken([]); }}
          onSubmit={(body) => {
            void run(async () => {
              const made = await api.generateUnits(body);
              await reload();
              // Clashes keep the form open, because the operator has to see
              // which addresses were left alone — the rest of the range did
              // land, and closing the form would hide that entirely.
              setTaken(made.alreadyTaken);
              if (made.alreadyTaken.length === 0) setGenerating(false);
              return made;
            }, (made) => t('app.unitsGenerated', { count: made.created }));
          }}
        />
      )}

      {terrainsOpen && (
        <Terrains
          groups={groups}
          units={units}
          onClose={() => setTerrainsOpen(false)}
          onSave={(g) => {
            void run(async () => {
              await api.putUnitGroup(g);
              await reload();
              return true;
            }, t('app.terrainSaved'));
          }}
        />
      )}
    </div>
  );
}

function blankUnit(accommodationId: string, terrain: string): UnitDto {
  return {
    id: '',
    accommodationId,
    groupId: terrain && terrain !== 'none' ? terrain : null,
    number: null,
    label: '',
    address: '',
    active: true,
    selectable: false,
    sort: 0,
  };
}

/**
 * The fast path: block a unit for a range, with a reason.
 *
 * The unit is TYPED, not picked from a list. A park has hundreds of pitches and
 * reception knows which one they mean before they touch the keyboard —
 * "Berkenlaan 12" is four keystrokes with a datalist behind it, against a scroll
 * through a select of 387. Typing something that is not a pitch has to say so
 * plainly, which a select could never get wrong and a text field easily can.
 */
function BlockForm({
  units, allUnits, groupLabel, unitText, onUnitText, accommodation, onSubmit, busy,
}: {
  units: UnitDto[];
  allUnits: UnitDto[];
  groupLabel: (id: string | null | undefined) => string | null;
  unitText: string;
  onUnitText: (v: string) => void;
  accommodation: AccommodationDto | null;
  onSubmit: (block: BlockDto, unit: UnitDto) => void;
  busy?: boolean;
}) {
  const t = useT();
  const start = toIso(today());
  const [from, setFrom] = useState(start);
  const [until, setUntil] = useState(toIso(today() + 1));
  // A reason the API accepts, and the commonest one. The default used to be
  // `blocked`, which it refuses.
  const [reason, setReason] = useState<string>('maintenance');
  const [note, setNote] = useState('');

  /* Matched against the whole park, not the chosen type: reception says a pitch
     name, not "the pitch name of a comfort pitch". Getting the accommodation
     wrong should not turn a real address into "no such unit". */
  const wanted = unitText.trim().toLowerCase();
  const unit = wanted
    ? allUnits.find((u) => (u.address ?? '').toLowerCase() === wanted)
      ?? allUnits.find((u) => (u.label ?? '').toLowerCase() === wanted)
      ?? allUnits.find((u) => u.id.toLowerCase() === wanted)
      ?? null
    : null;

  const f = toDay(from);
  const u = toDay(until);
  const nights = f !== null && u !== null ? u - f : 0;
  const problem: MessageKey | null = !wanted ? 'units.blockNeedsUnit'
    : !unit ? 'units.blockNoSuchUnit'
      : nights < 1 ? 'units.blockNeedsNights'
        : null;

  return (
    <section className="panel">
      <div className="panel-hd">
        <h2>{t('units.blockHeading')}</h2>
        <span className="grow" />
        <span className="note">{t('units.blockNote')}</span>
      </div>
      <div className="panel-bd">
        <div className="controls">
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="blk-unit">{t('units.blockUnit')}</label>
            <input
              id="blk-unit" list="blk-units" value={unitText}
              placeholder={t('units.blockUnitPlaceholder')}
              onChange={(e) => onUnitText(e.target.value)}
            />
            {/* The whole park, so a pitch can be found without first choosing
                the right accommodation above. */}
            <datalist id="blk-units">
              {allUnits.map((x) => (
                <option key={x.id} value={x.address ?? x.label ?? x.id} />
              ))}
            </datalist>
          </div>
          <div className="field">
            <label htmlFor="blk-from">{t('units.blockFrom')}</label>
            <input
              id="blk-from" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="blk-until">{t('units.blockUntil')}</label>
            <input
              id="blk-until" type="date" value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="blk-reason">{t('units.blockReason')}</label>
            <select id="blk-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
              {REASONS.map((r) => (
                <option key={r} value={r}>{reasonText(t, r)}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="blk-note">{t('units.blockNoteField')}</label>
            <input
              id="blk-note" value={note} placeholder={t('units.blockNotePlaceholder')}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <div className="row" style={{ marginTop: 14 }}>
          <button
            type="button" className="btn pri" disabled={problem !== null || busy}
            onClick={() => {
              if (!unit || f === null || u === null) return;
              onSubmit({
                // One block per unit per start date. Entering the same start
                // twice REPLACES rather than stacking a second row on top of
                // the first, which is what an operator correcting a date they
                // just typed means by it.
                id: `blk-${unit.id}-${from}`,
                unitId: unit.id, from, until, reason, note: note.trim() || undefined,
              }, unit);
              // The pitch is cleared and everything else kept, because the real
              // job is "the same works close five pitches": naming the next one
              // is then two keystrokes. It is also the visible sign that the
              // last one went through, alongside the banner.
              onUnitText('');
            }}
          >
            {t('units.blockSubmit')}
          </button>
          {/* One line that is either the confirmation or the reason there is
              none. An operator on the phone reads this instead of the form. */}
          {problem ? (
            <span className="note">{t(problem, { text: unitText.trim() })}</span>
          ) : unit && (
            <span className="note">
              {t('units.blockSummary', {
                unit: unit.display ?? unit.id,
                terrain: groupLabel(unit.groupId) ?? t('units.noTerrain'),
                nights: t('quote.nightCount', { count: Math.max(nights, 0) }),
              })}
              {unit.accommodationId !== accommodation?.id && (
                <> {t('units.blockOtherType')}</>
              )}
            </span>
          )}
        </div>
        {units.length === 0 && (
          <p className="note" style={{ marginTop: 10 }}>{t('units.blockNothingToBlock')}</p>
        )}
      </div>
    </section>
  );
}

function UnitForm({ unit, groups, allUnits, onSubmit, onCancel, onDelete, busy }: {
  unit: UnitDto;
  groups: UnitGroupDto[];
  allUnits: UnitDto[];
  onSubmit: (u: UnitDto) => void;
  onCancel: () => void;
  /** Absent for a unit that does not exist yet. */
  onDelete?: () => void;
  busy?: boolean;
}) {
  const t = useT();
  const [draft, setDraft] = useState<UnitDto>(unit);
  const isNew = unit.id === '';
  const set = (patch: Partial<UnitDto>) => setDraft((d) => ({ ...d, ...patch }));

  /* Fixed for the life of the form. Derived from the clock only because a unit
     with no number has nothing else to be distinguished by — recomputing it per
     render would make the identifier on screen change under the operator. */
  const [stamp] = useState(() => Date.now().toString(36));

  /**
   * The id, for a unit that has none yet.
   *
   * Derived from the terrain and the number, which is how the range generator
   * names them, so a unit added by hand next to a generated terrain fits the
   * same pattern. It is an identifier and never shown to a guest, so it stays
   * ASCII and does not follow the address.
   */
  const id = isNew
    ? `${draft.groupId ?? draft.accommodationId}-${draft.number ?? stamp}`
    : draft.id;

  const address = (draft.address ?? '').trim();
  /* Addresses are unique across the PARK — the database says so with an index,
     and the API answers a clash with a 500 that tells an operator nothing. So
     the clash is caught here, where the whole park is in hand and there is room
     to say which unit already has that name. */
  const clash = address
    ? allUnits.find((u) => u.id !== id && (u.address ?? '').toLowerCase() === address.toLowerCase())
    : undefined;
  const idTaken = isNew ? allUnits.some((u) => u.id === id) : false;
  const nameless = !address && !(draft.label ?? '').trim();

  return (
    <section className="panel">
      <div className="panel-hd">
        <h2>
          {isNew
            ? t('units.newHeading')
            : t('units.editHeading', { label: unit.display ?? unit.id })}
        </h2>
      </div>
      <div className="panel-bd">
        <div className="controls">
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="u-address">{t('units.address')}</label>
            <input
              id="u-address" value={draft.address ?? ''} placeholder={t('units.addressPlaceholder')}
              onChange={(e) => set({ address: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="u-terrain">{t('units.terrain')}</label>
            <select
              id="u-terrain" value={draft.groupId ?? ''}
              onChange={(e) => set({ groupId: e.target.value || null })}
            >
              <option value="">{t('units.noTerrain')}</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="u-number">{t('units.number')}</label>
            <input
              id="u-number" type="number" value={draft.number ?? ''}
              onChange={(e) => set({ number: e.target.value === '' ? null : Number(e.target.value) })}
            />
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="u-label">{t('units.name')}</label>
            <input
              id="u-label" value={draft.label ?? ''} placeholder={t('units.namePlaceholder')}
              onChange={(e) => set({ label: e.target.value })}
            />
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="u-note">{t('units.noteField')}</label>
            <input
              id="u-note" value={draft.note ?? ''} placeholder={t('units.notePlaceholder')}
              onChange={(e) => set({ note: e.target.value })}
            />
          </div>
        </div>

        <p className="note" style={{ marginTop: 10 }}>{t('units.addressNote')}</p>

        <label className="check" style={{ marginTop: 12 }}>
          <input
            type="checkbox" checked={draft.active !== false}
            onChange={(e) => set({ active: e.target.checked })}
          />
          {t('units.active')}
        </label>
        <label className="check" style={{ marginTop: 8 }}>
          <input
            type="checkbox" checked={draft.selectable === true}
            onChange={(e) => set({ selectable: e.target.checked })}
          />
          {t('units.selectable')}
        </label>

        {isNew && (
          <p className="note" style={{ marginTop: 12 }}>
            {t('units.identifierIs', { id })}
          </p>
        )}

        {(nameless || clash || idTaken) && (
          <div className="diag" style={{ marginTop: 12 }}>
            {nameless && (
              <div className="dg error">
                <span className="ic" aria-hidden="true">✕</span>
                <span>{t('units.needsAName')}</span>
              </div>
            )}
            {clash && (
              <div className="dg error">
                <span className="ic" aria-hidden="true">✕</span>
                <span>{t('units.addressTaken', { address, unit: clash.id })}</span>
              </div>
            )}
            {idTaken && (
              <div className="dg error">
                <span className="ic" aria-hidden="true">✕</span>
                <span>{t('units.identifierTaken', { id })}</span>
              </div>
            )}
          </div>
        )}

        <div className="row" style={{ marginTop: 14 }}>
          <button
            type="button" className="btn pri"
            disabled={nameless || Boolean(clash) || idTaken}
            onClick={() => onSubmit({ ...draft, id, address: address || null })}
          >
            {t('units.save')}
          </button>
          <button type="button" className="btn" onClick={onCancel}>{t('units.cancel')}</button>
          {onDelete && (
            <>
              <span className="grow" />
              {/* Here as well as on the row, and the ONLY place on a phone: the
                  row's version is hidden there rather than sitting one thumb
                  away from Edit. */}
              <button type="button" className="btn danger" disabled={busy} onClick={onDelete}>
                {t('units.deleteThis')}
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function GenerateForm({ accommodationId, groups, preferGroup, taken, onSubmit, onCancel }: {
  accommodationId: string;
  groups: UnitGroupDto[];
  preferGroup: string;
  taken: string[];
  onSubmit: (b: {
    accommodationId: string; groupId?: string; prefix?: string; from: number; to: number;
  }) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [groupId, setGroupId] = useState(preferGroup || (groups[0]?.id ?? ''));
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(20);
  const [prefix, setPrefix] = useState('');

  const count = to - from + 1;
  const tooMany = count > 500;
  const backwards = count < 1;
  const example = `${prefix || groups.find((g) => g.id === groupId)?.label || ''} ${from}`.trim();

  return (
    <section className="panel">
      <div className="panel-hd"><h2>{t('units.generateHeading')}</h2></div>
      <div className="panel-bd">
        <p className="note" style={{ marginBottom: 12 }}>{t('units.generateNote')}</p>
        <div className="controls">
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="g-terrain">{t('units.terrain')}</label>
            <select id="g-terrain" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">{t('units.noTerrain')}</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="g-from">{t('units.generateFrom')}</label>
            <input
              id="g-from" type="number" value={from}
              onChange={(e) => setFrom(Number(e.target.value) || 0)}
            />
          </div>
          <div className="field">
            <label htmlFor="g-to">{t('units.generateTo')}</label>
            <input
              id="g-to" type="number" value={to}
              onChange={(e) => setTo(Number(e.target.value) || 0)}
            />
          </div>
          <div className="field" style={{ gridColumn: 'span 2' }}>
            <label htmlFor="g-prefix">{t('units.generatePrefix')}</label>
            <input
              id="g-prefix" value={prefix} placeholder={t('units.generatePrefixPlaceholder')}
              onChange={(e) => setPrefix(e.target.value)}
            />
          </div>
        </div>
        <p className="note" style={{ marginTop: 10 }}>
          {backwards
            ? t('units.generateBackwards')
            : tooMany
              ? t('units.generateTooMany')
              : t('units.generateCount', { count, example })}
        </p>

        {taken.length > 0 && (
          <div className="diag" style={{ marginTop: 12 }}>
            <div className="dg warning">
              <span className="ic" aria-hidden="true">!</span>
              <span>
                {t('units.someTaken', { count: taken.length, list: taken.slice(0, 8).join(', ') })}
              </span>
            </div>
          </div>
        )}

        <div className="row" style={{ marginTop: 14 }}>
          <button
            type="button" className="btn pri" disabled={tooMany || backwards}
            onClick={() => onSubmit({
              accommodationId, groupId: groupId || undefined,
              prefix: prefix.trim() || undefined, from, to,
            })}
          >
            {t('units.generate')}
          </button>
          <button type="button" className="btn" onClick={onCancel}>{t('units.cancel')}</button>
        </div>
      </div>
    </section>
  );
}

/**
 * The terrains, and their order.
 *
 * Two in one park may not share a name — the database has a unique index on it —
 * and the API answers that clash with a 500 and no explanation, so the clash is
 * caught here where the other names are already known.
 */
function Terrains({ groups, units, onSave, onClose }: {
  groups: UnitGroupDto[];
  units: UnitDto[];
  onSave: (g: UnitGroupDto) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState<UnitGroupDto | null>(null);

  const count = (id: string) => units.filter((u) => u.groupId === id).length;
  const name = (draft?.label ?? '').trim();
  const clash = name
    ? groups.find((g) => g.id !== draft?.id && g.label.toLowerCase() === name.toLowerCase())
    : undefined;

  return (
    <section className="panel">
      <div className="panel-hd">
        <h2>{t('units.terrainsHeading')}</h2>
        <span className="grow" />
        <button
          type="button" className="btn pri"
          onClick={() => setDraft({
            id: `terrain-${Date.now().toString(36)}`,
            label: '',
            sort: (groups.length + 1) * 10,
          })}
        >
          {t('units.addTerrain')}
        </button>
        <button type="button" className="btn" onClick={onClose}>{t('units.close')}</button>
      </div>
      <div className="panel-bd">
        <p className="note" style={{ marginBottom: 12 }}>{t('units.terrainsNote')}</p>
        {groups.length === 0 ? (
          <p className="note">{t('units.noTerrains')}</p>
        ) : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th>{t('units.terrainName')}</th>
                  <th className="r">{t('units.terrainOrder')}</th>
                  {/* How many units sit in it — a count, so the heading is the
                      plural noun rather than the single-unit column's label. */}
                  <th className="r">{t('units.heading')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.id} className={draft?.id === g.id ? 'picked' : undefined}>
                    <td>{g.label}</td>
                    <td className="r">{g.sort ?? 0}</td>
                    <td className="r">{t('units.count', { count: count(g.id) })}</td>
                    <td className="r">
                      <button type="button" className="btn" onClick={() => setDraft(g)}>
                        {t('units.edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {draft && (
          <div style={{ marginTop: 16 }}>
            <div className="controls">
              <div className="field" style={{ gridColumn: 'span 2' }}>
                <label htmlFor="tg-label">{t('units.terrainName')}</label>
                <input
                  id="tg-label" value={draft.label} placeholder={t('units.terrainNamePlaceholder')}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="tg-sort">{t('units.terrainOrder')}</label>
                <input
                  id="tg-sort" type="number" value={draft.sort ?? 0}
                  onChange={(e) => setDraft({ ...draft, sort: Number(e.target.value) || 0 })}
                />
              </div>
            </div>
            {clash && (
              <div className="diag" style={{ marginTop: 12 }}>
                <div className="dg error">
                  <span className="ic" aria-hidden="true">✕</span>
                  <span>{t('units.terrainNameTaken', { label: name })}</span>
                </div>
              </div>
            )}
            <div className="row" style={{ marginTop: 14 }}>
              <button
                type="button" className="btn pri" disabled={!name || Boolean(clash)}
                onClick={() => { onSave({ ...draft, label: name }); setDraft(null); }}
              >
                {t('units.saveTerrain')}
              </button>
              <button type="button" className="btn" onClick={() => setDraft(null)}>
                {t('units.cancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
